import { ActionIcon, Badge, Group, Loader, Stack, Text, Tooltip } from "@mantine/core";
import { Check, Download, Save } from "lucide-react";
import { useState } from "react";

import { useListTemplates } from "../../api/generated/endpoints/generation/generation";
import { useListMembers } from "../../api/generated/endpoints/teams/teams";
import type { GenerationOutput, Job } from "../../api/generated/model";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { useRowActions } from "../../platform/jobs/GenerationActions";
import { failedWords } from "../../platform/jobs/failed-words";
import { outputMedium } from "../../platform/jobs/detail/outputs";
import { GENERATIONS_PAGE, useJobList } from "../../platform/jobs/job-list";
import { isSettled } from "../../platform/jobs/status";
import { outputKey } from "../../platform/jobs/output-picks";
import { JobFacts } from "../../platform/media/GenerationFacts";
import { MediaPreview, type PreviewItem } from "../../platform/media/MediaPreview";
import { previewKind } from "../../platform/media/output-kind";
import { memberName } from "../../platform/members";
import { useStreamStatus } from "../../platform/stream-status";
import { EmptyState } from "../../ui/EmptyState";
import { formatDateTime, formatDay } from "../../ui/format";
import { LoadAhead, morePages } from "../../ui/LoadAhead";
import { MediaCard, MediaExpired, MediaFailed, MediaLoading } from "../../ui/MediaCard";
import { expiredLine, isExpired } from "../../platform/jobs/expired";
import { VideoScrubber } from "../../ui/VideoScrubber";
import { ThumbImage } from "../../ui/ThumbImage";
import classes from "./templates.module.css";

/** The library's every card at once (63), for the rows' titles: the route's largest page. */
const EVERY = 100;
/** Both media's template runs, as live's My Generations lists them together. */
const TEMPLATE_TYPES = ["image.template", "video.template"] as const;
/** A run of a template the catalog no longer lists: live's badge. */
const UNNAMED = "Template";

/** A tile that shows its picture. */
const ready = (output: GenerationOutput) => output.status === "delivered" && output.url !== null;

export interface TemplateGenerationsProps {
  teamId: string;
  brandId: string;
}

/**
 * Live's My Generations on the Templates page (`tabs/my-generations`, capture 04): the brand's
 * template runs, newest first, kept live by the stream, live's 5 a page, the next as the end
 * shows (`ui/LoadAhead`). Each run is a row:
 * its template's title (live's "Template" when the catalog has none), when, and live's "Save
 * generation", "Download generation" and "Delete generation"; then its tiles, 5 across at 1:1,
 * each waiting, failed with live's words, or its picture with live's Save and Download, the
 * saved ones ringed. A ready tile opens the shared preview.
 */
export function TemplateGenerations({ teamId, brandId }: TemplateGenerationsProps) {
  const jobs = useJobList(
    teamId,
    { status: "all", brandId, types: TEMPLATE_TYPES, pageSize: GENERATIONS_PAGE },
    useStreamStatus(),
  );
  const more = morePages(jobs);
  const titles = useListTemplates(teamId, { limit: EVERY });
  const [previewing, setPreviewing] = useState<string | null>(null);
  const items = jobs.data?.pages.flatMap((page) => page.items) ?? [];
  if (jobs.isPending) return <Loader size="sm" aria-label="Loading your generations" />;
  if (jobs.isError) return <ErrorAlert error={jobs.error} />;
  if (items.length === 0 && !jobs.hasNextPage) {
    return (
      <EmptyState
        title="No generations yet"
        description="Generate something from a template and it will appear here."
      />
    );
  }
  // The title the run kept at its start, which a retired template's runs still read (as live's
  // list resolves a deleted template); a run from before it kept one, the library's.
  const titleOf = (job: Job) => {
    const kept = job.params?.template_title;
    if (kept !== null && kept !== undefined) return kept;
    const id = job.params?.template?.template;
    return titles.data?.items.find((card) => card.id === id)?.title ?? UNNAMED;
  };
  const readAgain = () => void jobs.refetch();
  const shown = items.flatMap((job) =>
    job.outputs.filter(ready).map((output) => ({ job, output, item: previewOf(job, output, titleOf(job)) })),
  );
  return (
    <Stack gap={0} className={classes.runs} aria-label="My Generations">
      {items.map((job) => (
        <RunRow
          key={job.id}
          teamId={teamId}
          brandId={brandId}
          job={job}
          title={titleOf(job)}
          readAgain={readAgain}
          onOpen={(output) => setPreviewing(outputKey({ jobId: job.id, index: output.index }))}
        />
      ))}
      {/* Live's: the next page as the end shows (`InfiniteScroll`), no button. */}
      <LoadAhead more={more} loaded={items.length} label="Loading more generations" />
      <RunPreview
        teamId={teamId}
        brandId={brandId}
        tiles={shown}
        current={previewing}
        onShow={setPreviewing}
        more={more}
        readAgain={readAgain}
      />
    </Stack>
  );
}

function previewOf(job: Job, output: GenerationOutput, title: string): PreviewItem {
  return {
    id: outputKey({ jobId: job.id, index: output.index }),
    name: job.outputs.length > 1 ? `${title} (${output.index + 1})` : title,
    // A video template's tile is a video (Animate Static Ad's clip), a picture template's a picture.
    kind: previewKind(output) ?? "image",
    url: output.url,
    // The strip's lighter pictures (issue 828, item 12): the thumbnail, else the picture itself.
    thumb: output.thumb_url ?? (previewKind(output) === "video" ? null : output.url),
    poster: output.poster_url ?? null,
    durationMs: output.duration_ms,
    width: output.width,
    height: output.height,
  };
}

interface RunRowProps {
  teamId: string;
  brandId: string;
  job: Job;
  title: string;
  readAgain: () => void;
  onOpen: (output: GenerationOutput) => void;
}

/** One run: live's header and actions, then its tiles in slot order. */
function RunRow({ teamId, brandId, job, title, readAgain, onOpen }: RunRowProps) {
  const row = useRowActions({ teamId, brandId, jobs: [job], readAgain, toProject: false });
  const when = formatDateTime(job.created_at);
  const savedOf = (output: GenerationOutput) =>
    (output.saved_asset_id ?? null) !== null || row.saved.has(outputKey({ jobId: job.id, index: output.index }));
  return (
    <article className={classes.run} aria-label={`${title}, ${when}`}>
      <Group justify="space-between" wrap="nowrap" mb={12}>
        <Group gap={8} wrap="nowrap" miw={0}>
          <Badge variant="light" radius="xl" className={classes.runBadge}>
            {title}
          </Badge>
          <Text size="xs" c="var(--cai-color-text-muted)" truncate>
            {when}
          </Text>
        </Group>
        {row.icons}
      </Group>
      {row.notes}
      <div className={classes.tiles}>
        {job.outputs.map((output) => {
          if (isExpired(output)) {
            return <MediaExpired key={output.index} title={expiredLine(job)} medium={outputMedium(job)} />;
          }
          // A tile the run left (canceled, or out of time) failed with the run.
          if (output.status === "failed" || (!ready(output) && isSettled(job.status))) {
            const message = output.error?.message ?? job.error?.message;
            return (
              <MediaFailed key={output.index} {...failedWords(job.type, message)} medium={outputMedium(job)} />
            );
          }
          if (!ready(output)) {
            return (
              <MediaLoading
                key={output.index}
                aspectRatio="1:1"
                label={`Tile ${output.index + 1}, in progress`}
                medium={outputMedium(job)}
              />
            );
          }
          const saved = savedOf(output);
          const pick = { jobId: job.id, index: output.index };
          const menu = (
            <TileMenu
              saved={saved}
              saving={row.saving}
              onSave={() => row.saveOne(pick)}
              onDownload={() => row.downloadOne(job, output)}
            />
          );
          if (previewKind(output) === "video") {
            // Live's tile is the Video page's card (`VideoGenerationCard`): the scrubber (the
            // pointer picks the time, held still it plays, muted), no browser controls, and a
            // click opens the preview, as a picture's tile does.
            return (
              <MediaCard key={output.index} saved={saved} menu={menu}>
                <button
                  type="button"
                  className={classes.tile}
                  aria-label={`Open ${title} ${output.index + 1}`}
                  onClick={() => onOpen(output)}
                >
                  <VideoScrubber
                    src={output.url!}
                    // Its 512 px first frame, until the pointer scrubs it (issue 828, item 12).
                    poster={output.thumb_url ?? output.poster_url ?? null}
                    durationMs={output.duration_ms}
                    label={`${title} ${output.index + 1}`}
                    fallback={<span className={classes.tileMissing}>The preview did not load.</span>}
                    onFailed={readAgain}
                  />
                </button>
              </MediaCard>
            );
          }
          return (
            <MediaCard key={output.index} saved={saved} menu={menu}>
              <button
                type="button"
                className={classes.tile}
                aria-label={`Open ${title} ${output.index + 1}`}
                onClick={() => onOpen(output)}
              >
                <ThumbImage thumb={output.thumb_url} full={output.url!} alt="" className={classes.tileImage} onLost={readAgain} />
              </button>
            </MediaCard>
          );
        })}
      </div>
    </article>
  );
}

interface TileMenuProps {
  saved: boolean;
  saving: boolean;
  onSave: () => void;
  onDownload: () => void;
}

/** Live's tile pill: Save (then "Saved", grey) and Download. */
function TileMenu({ saved, saving, onSave, onDownload }: TileMenuProps) {
  return (
    <Group gap={4} wrap="nowrap">
      <Tooltip label={saved ? "Saved" : "Save"}>
        <ActionIcon variant="text-white" aria-label={saved ? "Saved" : "Save"} disabled={saved} loading={saving} onClick={onSave}>
          {saved ? <Check size={18} /> : <Save size={18} />}
        </ActionIcon>
      </Tooltip>
      <Tooltip label="Download">
        <ActionIcon variant="text-white" aria-label="Download" onClick={onDownload}>
          <Download size={18} />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
}

interface PreviewTile {
  job: Job;
  output: GenerationOutput;
  item: PreviewItem;
}

interface RunPreviewProps {
  teamId: string;
  brandId: string;
  tiles: readonly PreviewTile[];
  current: string | null;
  onShow: (id: string | null) => void;
  more: { has: boolean; loading: boolean; load: () => void };
  readAgain: () => void;
}

/** The shared preview of the runs' ready tiles: who made it and when, Save, Download, and ⓘ. */
function RunPreview({ teamId, brandId, tiles, current, onShow, more, readAgain }: RunPreviewProps) {
  const members = useListMembers(teamId, { query: { enabled: current !== null } }).data ?? [];
  const find = (item: PreviewItem) => tiles.find((tile) => tile.item.id === item.id);
  return (
    <MediaPreview
      items={tiles.map((tile) => tile.item)}
      current={current}
      onShow={onShow}
      more={more}
      byline={(item) => {
        const tile = find(item);
        if (tile === undefined) return null;
        const maker = tile.job.created_by;
        const name = maker === null ? "Unknown" : memberName(members.find((member) => member.user_id === maker));
        return `${name} · ${formatDay(tile.job.created_at)}`;
      }}
      actions={(item) => {
        const tile = find(item);
        return tile === undefined ? null : (
          <PreviewBar key={item.id} teamId={teamId} brandId={brandId} tile={tile} readAgain={readAgain} />
        );
      }}
      details={(item) => {
        const tile = find(item);
        return tile === undefined ? null : <JobFacts teamId={teamId} brandId={brandId} jobId={tile.job.id} />;
      }}
    />
  );
}

function PreviewBar(props: { teamId: string; brandId: string; tile: PreviewTile; readAgain: () => void }) {
  const { job, output } = props.tile;
  const row = useRowActions({ ...props, jobs: [job], toProject: false });
  const saved =
    (output.saved_asset_id ?? null) !== null || row.saved.has(outputKey({ jobId: job.id, index: output.index }));
  return (
    <TileMenu
      saved={saved}
      saving={row.saving}
      onSave={() => row.saveOne({ jobId: job.id, index: output.index })}
      onDownload={() => row.downloadOne(job, output)}
    />
  );
}
